// Shared display formatting (TimeKeep Page System spec, "Shared formatting").
//
// Pure functions, no colors -- safe to call anywhere, including module scope.
// Anything that needs a color returns a tone NAME ('danger', 'warn', 'good'),
// which ui.js's toneColor() turns into a color at render time.
//
// Money uses the org's own currency (organizations.currency). Before this
// module every page hardcoded "$". setFormatContext() is called next to
// applyTheme(org), so every formatter picks it up.

import { BILLING_MODELS } from './billingModels';

let ctx = { currency: 'USD', locale: 'en-US' };
const nfCache = new Map();

export function setFormatContext({ currency, locale } = {}) {
  ctx = { currency: currency || 'USD', locale: locale || 'en-US' };
}
export const formatCurrency = () => ctx.currency;

function nf(cents) {
  const key = `${ctx.locale}|${ctx.currency}|${cents}`;
  if (!nfCache.has(key)) {
    let f;
    try {
      f = new Intl.NumberFormat(ctx.locale, { style: 'currency', currency: ctx.currency,
        minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
    } catch (e) {
      // An unknown currency code must never break rendering.
      f = new Intl.NumberFormat(ctx.locale, { style: 'currency', currency: 'USD',
        minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
    }
    nfCache.set(key, f);
  }
  return nfCache.get(key);
}

const MINUS = '−';

// ── Money ──────────────────────────────────────────────────────────────────────
// Summaries (tiles, pills, report tables, charts): whole units.
// Line level (invoice lines, expenses, member invoice lines, receipts): { cents: true }.
export function fmtMoney(n, { cents = false } = {}) {
  const v = Number(n);
  if (n == null || n === '' || !isFinite(v)) return '—';
  const s = nf(cents).format(Math.abs(v));
  // Avoid "−$0" when rounding a tiny negative to zero.
  return v < 0 && s !== nf(cents).format(0) ? MINUS + s : s;
}

// "$275/hr", "$2,000/mo", "$42.50/hr" -- cents only when the rate isn't whole.
export function fmtRate(n, unit = 'hr') {
  const v = Number(n);
  if (n == null || n === '' || !isFinite(v)) return '—';
  return `${fmtMoney(v, { cents: Math.round(v * 100) % 100 !== 0 })}/${unit}`;
}

// Chart axis ticks: "$8k", "$1.2M", "$450".
export function fmtMoneyShort(n) {
  const v = Number(n) || 0, a = Math.abs(v), sign = v < 0 ? MINUS : '';
  const sym = nf(false).format(0).replace(/[\d\s.,]/g, '') || '$';
  if (a >= 1e6) return `${sign}${sym}${+(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}${sym}${+(a / 1e3).toFixed(a >= 1e4 ? 0 : 1)}k`;
  return `${sign}${sym}${Math.round(a)}`;
}

// ── Percent ────────────────────────────────────────────────────────────────────
// Whole numbers; "<1%" for tiny values; "—" when there's no base (null/NaN/∞).
export function fmtPercent(p) {
  const v = Number(p);
  if (p == null || !isFinite(v)) return '—';
  if (v !== 0 && Math.abs(v) < 1) return v < 0 ? `${MINUS}<1%` : '<1%';
  const r = Math.round(v);
  return r < 0 ? `${MINUS}${Math.abs(r)}%` : `${r}%`;
}

// ── Hours ──────────────────────────────────────────────────────────────────────
// "15h 1m" · "45m" · "3h" · "0m" -- the same for single entries and totals.
export function fmtHours(h) {
  const mins = Math.round((Number(h) || 0) * 60);
  const neg = mins < 0, m = Math.abs(mins);
  const hh = Math.floor(m / 60), mm = m % 60;
  const s = hh === 0 ? `${mm}m` : mm === 0 ? `${hh}h` : `${hh}h ${mm}m`;
  return neg ? MINUS + s : s;
}
// CSV and other machine-readable output: decimal hours, 2 places.
export const hoursDecimal = (h) => (Number(h) || 0).toFixed(2);

// ── Dates ──────────────────────────────────────────────────────────────────────
// Accepts 'YYYY-MM-DD' (parsed as a LOCAL date, so it never shifts a day across
// time zones), an ISO timestamp, or a Date.
export function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(v);
  return isNaN(d) ? null : d;
}
const todayDate = (today) => toDate(today) || new Date();

// "Sep 20" in the current year, "Sep 20, 2025" otherwise.
export function fmtDate(v, { today } = {}) {
  const d = toDate(v);
  if (!d) return '—';
  const sameYear = d.getFullYear() === todayDate(today).getFullYear();
  return d.toLocaleDateString(ctx.locale, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
}

// "2:25 PM"
export function fmtTime(v) {
  const d = toDate(v);
  return d ? d.toLocaleTimeString(ctx.locale, { hour: 'numeric', minute: '2-digit' }) : '';
}
// "2:25–2:32 PM" (shared suffix) or "11:50 AM–12:10 PM".
export function fmtTimeRange(a, b) {
  const s = fmtTime(a), e = fmtTime(b);
  if (!s || !e) return s || e || '';
  const sp = s.split(' '), ep = e.split(' ');
  return sp[1] && sp[1] === ep[1] ? `${sp[0]}–${e}` : `${s}–${e}`;
}

// "September 2026" (headings) / "Sep 2026" (tables, badges). Takes 'YYYY-MM'.
export function fmtPeriod(ym, { short = false } = {}) {
  if (!ym || !/^\d{4}-\d{2}/.test(ym)) return ym || '—';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(ctx.locale, { month: short ? 'short' : 'long', year: 'numeric' });
}

const DAY = 86400000;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// Due / paid line for an invoice. Returns { text, tone } -- tone is a NAME.
//   Paid Sep 18 · 12 days overdue · Due today · Due in 3 days · Due Oct 13
export function fmtDue(invoice, today) {
  if (!invoice) return { text: '', tone: 'neutral' };
  if (invoice.status === 'paid') {
    return { text: invoice.paid_at ? `Paid ${fmtDate(invoice.paid_at, { today })}` : 'Paid', tone: 'good' };
  }
  const due = toDate(invoice.due_date);
  if (!due) return { text: '—', tone: 'neutral' };
  if (invoice.status === 'draft') return { text: `Due ${fmtDate(due, { today })}`, tone: 'neutral' };
  const days = Math.round((startOfDay(due) - startOfDay(todayDate(today))) / DAY);
  if (days < 0) return { text: `${-days} day${days === -1 ? '' : 's'} overdue`, tone: 'danger' };
  if (days === 0) return { text: 'Due today', tone: 'warn' };
  if (days <= 7) return { text: `Due in ${days} day${days === 1 ? '' : 's'}`, tone: 'neutral' };
  return { text: `Due ${fmtDate(due, { today })}`, tone: 'neutral' };
}

// ── Billing models ─────────────────────────────────────────────────────────────
export const billingModelLabel = (id) => BILLING_MODELS[id]?.label || id || '';
